import {
  Box,
  Building2,
  ArrowLeftRight,
  MapPin,
  Route,
  BarChart3,
  Bell,
  Shield,
  Users,
  Smartphone,
} from 'lucide-react';

// SlotFleet Gold Logo Component - Slot Machine Style
export const SlotFleetLogo = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="50%" stopColor="#f59e0b" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
    </defs>
    <rect x="10" y="15" width="80" height="70" rx="8" fill="#1a1a1a" stroke="url(#goldGradient)" strokeWidth="3"/>
    <rect x="18" y="28" width="18" height="24" rx="3" fill="#0a0a0a" stroke="url(#goldGradient)" strokeWidth="1.5"/>
    <rect x="41" y="28" width="18" height="24" rx="3" fill="#0a0a0a" stroke="url(#goldGradient)" strokeWidth="1.5"/>
    <rect x="64" y="28" width="18" height="24" rx="3" fill="#0a0a0a" stroke="url(#goldGradient)" strokeWidth="1.5"/>
    <text x="27" y="46" fill="url(#goldGradient)" fontSize="16" fontWeight="bold" fontFamily="Arial">7</text>
    <text x="50" y="46" fill="url(#goldGradient)" fontSize="16" fontWeight="bold" fontFamily="Arial">7</text>
    <text x="73" y="46" fill="url(#goldGradient)" fontSize="16" fontWeight="bold" fontFamily="Arial">7</text>
    <rect x="25" y="62" width="50" height="12" rx="6" fill="url(#goldGradient)"/>
    <circle cx="85" y="45" r="8" fill="url(#goldGradient)"/>
    <path d="M82 45 L88 45 M85 42 L85 48" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Generated images
export const HERO_DASHBOARD = '/images/hero-dashboard.webp';
export const FEATURE_ROUTES = '/images/feature-routes.webp';
export const MOBILE_APP = '/images/mobile-app.webp';

// Animation variants
export const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

export const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Features data - SEO optimized for skill games management
export const features = [
  {
    icon: Box,
    title: 'Amusement Machine Inventory Tracking',
    description: 'Track every skill game, slot machine, claw machine, and coin pusher with serial numbers, locations, transfer history, and maintenance records.',
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
  },
  {
    icon: Building2,
    title: 'Amusement Games Accounting',
    description: 'Automated accounting for all collections, payouts, and partner splits. No more spreadsheets.',
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
  },
  {
    icon: MapPin,
    title: 'Location Portal & Employee Management',
    description: 'Give location owners their own portal to manage bookkeeping, employees, service calls, and daily reports.',
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
  },
  {
    icon: Bell,
    title: 'Real-time Notifications',
    description: 'Instant alerts for service calls, collections, payments, and subscription events. Email + in-app.',
    color: 'text-rose-500',
    bg: 'bg-rose-500/10',
  },
  {
    icon: Shield,
    title: 'Role-Based Access Control',
    description: 'Configure permissions per role and company. Control who sees what with a visual permission matrix.',
    color: 'text-green-500',
    bg: 'bg-green-500/10',
  },
  {
    icon: ArrowLeftRight,
    title: 'Invoicing & Payment History',
    description: 'Generate invoices, track payment history, record partial payments, and export PDF statements.',
    color: 'text-teal-500',
    bg: 'bg-teal-500/10',
  },
  {
    icon: Route,
    title: 'Route Management',
    description: 'Plan efficient collection routes with Google Maps. Drag-to-reorder stops and assign drivers.',
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
  },
  {
    icon: BarChart3,
    title: 'Reports & Analytics',
    description: 'Interactive dashboard charts, machine accounting reports, partner payouts, and CSV/PDF exports.',
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
  },
  {
    icon: Smartphone,
    title: 'Mobile Friendly',
    description: 'Fully responsive design works on any device. Native iOS and Android apps via Capacitor.',
    color: 'text-cyan-500',
    bg: 'bg-cyan-500/10',
  },
];

// Testimonials
export const testimonials = [
  {
    quote: "SlotFleet transformed how we manage our slot machine inventory across 50+ locations. Finally, software built for our industry.",
    author: "Michael Torres",
    role: "Operations Director, Golden Games LLC",
    rating: 5,
  },
  {
    quote: "The machine accounting reports are incredible. We used to spend days on spreadsheets. Now it takes minutes.",
    author: "Sarah Chen",
    role: "CEO, Lucky Star Amusements",
    rating: 5,
  },
  {
    quote: "Managing multiple companies and partner payouts was a nightmare before SlotFleet. Now it's completely automated.",
    author: "James Wilson",
    role: "Owner, Vegas Connect",
    rating: 5,
  },
];

// Pricing tiers
export const pricingTiers = [
  {
    name: 'Location',
    tier: 'location',
    price: 29.99,
    annual: 287.90,
    description: 'For independent location owners',
    features: [
      '1 Location Included',
      '+$20/mo Per Extra Location',
      'Add & Manage Machines',
      'Money In/Out Accounting',
      'Employee Management',
      'Bookkeeping & Reports',
      'Service Call Tracking',
      'Real-time Notifications',
    ],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Basic',
    tier: 'basic',
    price: 199,
    annual: 1910,
    description: 'Everything you need to get started',
    features: [
      'Up to 10 Locations',
      'Money In/Out Tracking',
      'Dashboard with Charts',
      'Inventory Management',
      'Basic Reporting & CSV Export',
      'Real-time Notifications',
      'Search, Sort & Bulk Operations',
      'Mobile-Friendly Interface',
      'Email Support',
    ],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Pro',
    tier: 'pro',
    price: 499,
    annual: 4790,
    description: 'Advanced features for growing businesses',
    features: [
      'Up to 20 Locations',
      'Everything in Basic',
      'Location Portal & Employee Management',
      'Expense Management with Receipts',
      'Issue Tracking & Service Calls',
      'Partner Management & Payouts',
      'User Management & RBAC',
      'Machine Accounting Reports',
      'Activity Logs & Audit Trail',
      'Email Notifications',
      'Priority Support',
    ],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    name: 'Enterprise',
    tier: 'enterprise',
    price: 999,
    annual: 9590,
    isContactUs: false,
    description: 'Full suite for large-scale operations',
    features: [
      'Up to 50 Locations',
      'Everything in Pro',
      'Route Planning with Google Maps',
      'Invoicing & Payment History',
      'Multi-Company Support',
      'Custom Integrations',
      'Dedicated Account Manager',
      'SLA & Priority Support',
    ],
    cta: 'Start Free Trial',
    popular: false,
  },
];

// FAQ data - SEO optimized for skill games keywords
export const faqs = [
  {
    q: 'What is skill games management software?',
    a: 'Amusement games management software like SlotFleet is a comprehensive platform that helps operators manage skill games, slot machines, casino games, claw machines, and coin pushers. It includes accounting, invoicing, inventory tracking, machine monitoring, route management, and detailed reporting - all in one place.',
  },
  {
    q: 'How does SlotFleet help with skill games accounting?',
    a: 'SlotFleet automates your amusement games accounting by tracking all money-in and money-out transactions, calculating net profits per machine and location, generating partner splits, and creating detailed financial reports. No more spreadsheets or manual calculations.',
  },
  {
    q: 'Can I track individual skill game machines and inventory?',
    a: "Yes! Our inventory management system lets you track every cabinet and game board with serial numbers, current locations, transfer history, and maintenance records. Know exactly where each machine is and how it's performing.",
  },
  {
    q: 'Does SlotFleet support route management for skill games?',
    a: 'Absolutely! SlotFleet includes route management features that help you plan efficient collection routes, assign drivers, track service schedules, and optimize your skill games operation for maximum efficiency.',
  },
  {
    q: 'Can location owners access the skill games data?',
    a: 'Yes! Our Location Portal feature gives location owners their own login to view collections, track revenue, submit service calls, and manage their relationship with you - all in real-time.',
  },
  {
    q: 'How does invoicing work for skill games operations?',
    a: 'SlotFleet automatically generates invoices based on your collection data and partner agreements. Set up revenue splits, generate monthly statements, and track payments - all automated.',
  },
  {
    q: 'Is there a free trial for skill games management?',
    a: 'Yes! We offer a 7-day free trial with full access to your selected plan. Card required at signup, but you can cancel anytime during the trial.',
  },
  {
    q: 'How secure is my skill games business data?',
    a: 'Enterprise-grade security including multi-factor authentication (MFA), encrypted data transfer, role-based access control, daily automated backups, and complete audit trails of all activity.',
  },
];
